import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function NewsletterFormInner({ isDark }) {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setSubscribing(true);
    try {
      let recaptchaToken = null;
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("newsletter_subscribe");
      }
      
      await subscribeNewsletter({ 
        email: email.trim(),
        recaptcha_token: recaptchaToken
      });
      toast.success("Je bent ingeschreven voor onze nieuwsbrief!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Dit emailadres is al ingeschreven");
      } else {
        toast.error("Kon niet inschrijven. Probeer het later opnieuw.");
      }
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <Input 
        type="email" 
        placeholder="Je email adres" 
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={`${isDark ? 'bg-slate-800 border-white/10 text-white placeholder:text-slate-500' : 'bg-white border-slate-300'}`}
        required
      />
      <Button 
        type="submit" 
        disabled={subscribing}
        className="bg-[hsl(48,96%,53%)] hover:bg-[hsl(48,96%,43%)] text-slate-900 font-medium"
      >
        {subscribing ? "..." : "Aanmelden"}
      </Button>
    </form>
  );
}

// Simple form without reCAPTCHA
function NewsletterFormSimple({ isDark }) {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setSubscribing(true);
    try {
      await subscribeNewsletter({ 
        email: email.trim(),
        recaptcha_token: null
      });
      toast.success("Je bent ingeschreven voor onze nieuwsbrief!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Dit emailadres is al ingeschreven");
      } else {
        toast.error("Kon niet inschrijven. Probeer het later opnieuw.");
      }
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <Input 
        type="email" 
        placeholder="Je email adres" 
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={`${isDark ? 'bg-slate-800 border-white/10 text-white placeholder:text-slate-500' : 'bg-white border-slate-300'}`}
        required
      />
      <Button 
        type="submit" 
        disabled={subscribing}
        className="bg-[hsl(48,96%,53%)] hover:bg-[hsl(48,96%,43%)] text-slate-900 font-medium"
      >
        {subscribing ? "..." : "Aanmelden"}
      </Button>
    </form>
  );
}

export default function NewsletterForm({ isDark }) {
  if (!RECAPTCHA_SITE_KEY) {
    return <NewsletterFormSimple isDark={isDark} />;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_SITE_KEY}
      language="nl"
      scriptProps={{
        async: true,
        defer: true,
        appendTo: "head",
      }}
    >
      <NewsletterFormInner isDark={isDark} />
    </GoogleReCaptchaProvider>
  );
}
